"use client";

import { RichText } from "@/components/RichText";
import { useProjectText } from "@/features/project/useProjectText";
import type { ItemProperties, POIPageContent } from "@/types";
import { fileIconPath, tableEntries } from "../../poiTable";

/**
 * A POI's attached documents.
 *
 * Structurally the same block as `POITable` — an array property shown as rows of
 * configured columns — but two columns are rendered specially: `fileName` becomes
 * a link to the entry's `fileUrl`, and `fileExtension` becomes a file-type icon.
 *
 * `fileUrl` is read off the entry whether or not it is a displayed column. Legacy
 * narrowed each row to the configured columns first, so the only way to get a
 * linked file name was to also show a column of raw URLs beside it.
 */
export function FilesList({
  properties,
  options,
}: {
  properties: ItemProperties;
  options: POIPageContent;
}) {
  const t = useProjectText();
  const columns = options.tableColumns ?? [];
  const rows = tableEntries(properties, options.field, columns);

  if (rows.length === 0) return null;

  return (
    <div className="mb-3 mt-3 overflow-x-auto">
      <table className="min-w-full text-left text-sm text-gray-500">
        <thead className="bg-gray-700 text-gray-300">
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                className="p-2 font-medium tracking-wider text-white"
              >
                {t(`table.column.${column}`, column)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {rows.map((row, index) => (
            <tr key={String(row.id ?? index)} className="hover:bg-gray-50">
              {columns.map((column) => (
                <td key={column} className="p-2">
                  <Cell column={column} row={row} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({
  column,
  row,
}: {
  column: string;
  row: Record<string, unknown>;
}) {
  const value = row[column];

  if (column === "fileExtension") {
    // Tenant data decides the file, so the set of sources is fixed and local —
    // but it's still a plain <img>, since next/image would optimise ten static
    // 24px icons for nothing.
    // eslint-disable-next-line @next/next/no-img-element
    return (
      <img src={fileIconPath(value)} alt={String(value ?? "")} className="h-7" />
    );
  }

  if (column === "fileName") {
    const href = typeof row.fileUrl === "string" ? row.fileUrl : null;
    return href ? (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-muted underline hover:text-black"
      >
        {String(value ?? "")}
      </a>
    ) : (
      <RichText html={typeof value === "string" ? value : ""} />
    );
  }

  return <>{String(value ?? "")}</>;
}
